import {
  TransactionFeePayer,
  TransactionOnchainStatus,
  type TransactionViewFee,
  type TransactionViewLine,
} from '@/api/transaction-views/queries';
import type { SuiNetwork } from '@/config/sui';

const FEE_PAYER_LABELS: Record<TransactionFeePayer, string> = {
  [TransactionFeePayer.Wallet]: 'Paid by you',
  [TransactionFeePayer.Sender]: 'Network fee paid by sender',
  [TransactionFeePayer.Sponsor]: 'Sponsored',
};

const REBATE_RECIPIENT_LABELS: Record<TransactionFeePayer, string> = {
  [TransactionFeePayer.Wallet]: 'to you',
  [TransactionFeePayer.Sender]: 'to the sender',
  [TransactionFeePayer.Sponsor]: 'Sponsored',
};

const NETWORK_LABELS: Record<SuiNetwork, string> = {
  mainnet: 'Sui Mainnet',
  testnet: 'Sui Testnet',
  devnet: 'Sui Devnet',
};

export const formatSignedAmount = (amount: string | null): string => {
  if (amount === null) {
    return '—';
  }
  return amount.startsWith('-') ? `−${amount.slice(1)}` : `+${amount}`;
};

export const formatSignedLineAmount = (
  line: Pick<TransactionViewLine, 'amount' | 'denomination'>,
): string => [formatSignedAmount(line.amount), line.denomination].filter(Boolean).join(' ');

export const isGasRebate = (fee: Pick<TransactionViewFee, 'amount'>): boolean =>
  fee.amount?.startsWith('-') ?? false;

export const signFeeValue = (value: string, payer: TransactionFeePayer): string => {
  const isRebate = value.startsWith('-');
  const unsignedValue = isRebate ? value.slice(1) : value;
  return payer === TransactionFeePayer.Wallet && !isRebate ? `-${unsignedValue}` : unsignedValue;
};

export const formatFee = (
  fee: Pick<TransactionViewFee, 'amount' | 'denomination' | 'payer'>,
): string => {
  const amount = [formatFeeAmount(fee), fee.denomination].filter(Boolean).join(' ');
  return `${amount} · ${formatFeePayer(fee)}`;
};

export const formatTransactionStatus = (status: TransactionOnchainStatus): string =>
  status === TransactionOnchainStatus.Success ? 'CONFIRMED' : 'FAILED';

export const formatTransactionNetwork = (network: SuiNetwork): string => NETWORK_LABELS[network];

const formatFeeAmount = ({
  amount,
  payer,
}: Pick<TransactionViewFee, 'amount' | 'payer'>): string => {
  if (amount === null) {
    return '—';
  }
  const value = signFeeValue(amount, payer);
  return payer === TransactionFeePayer.Wallet ? formatSignedAmount(value) : value;
};

const formatFeePayer = (fee: Pick<TransactionViewFee, 'amount' | 'payer'>): string =>
  isGasRebate(fee) ? REBATE_RECIPIENT_LABELS[fee.payer] : FEE_PAYER_LABELS[fee.payer];
